// Lightweight value picker built on DropdownMenu — replaces native <select>
// and the form-control look of a Select. Click (or tap / press) the trigger,
// pick from a menu. Two looks:
//   variant="inline"  → text + chevron, ghost styling (composer From/Signature)
//   variant="field"   → clean bordered button, full width (settings forms)

import { Check, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export interface MenuPickerOption {
  value: string;
  label: string;
  /** Optional secondary text shown muted to the right. */
  hint?: string;
}

export function MenuPicker({
  value,
  options,
  onChange,
  variant = 'field',
  placeholder = 'Select…',
  align = 'start',
  className,
  contentClassName,
  disabled,
}: {
  value: string | undefined;
  options: MenuPickerOption[];
  onChange: (value: string) => void;
  variant?: 'inline' | 'field';
  placeholder?: string;
  align?: 'start' | 'end' | 'center';
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
}) {
  const current = options.find((o) => o.value === value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1.5 outline-none transition-colors disabled:cursor-not-allowed disabled:opacity-50',
            variant === 'field'
              ? 'h-8 w-full justify-between rounded-md border bg-background px-2.5 text-sm hover:bg-accent/40 focus-visible:ring-1 focus-visible:ring-ring'
              : 'max-w-full rounded-md px-1.5 py-0.5 font-medium text-foreground hover:bg-accent/60 focus-visible:ring-1 focus-visible:ring-ring',
            className,
          )}
        >
          <span className={cn('truncate text-left', !current && 'text-muted-foreground')}>
            {current?.label ?? placeholder}
          </span>
          <ChevronDown className="size-3.5 shrink-0 opacity-50" strokeWidth={2} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className={cn('max-h-72 min-w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto', contentClassName)}
      >
        {options.map((o) => (
          <DropdownMenuItem
            key={o.value}
            onClick={() => onChange(o.value)}
            className="gap-2"
          >
            <Check
              className={cn('size-3.5 shrink-0', o.value === value ? 'opacity-100' : 'opacity-0')}
              strokeWidth={2.5}
            />
            <span className="min-w-0 flex-1 truncate">{o.label}</span>
            {o.hint && <span className="shrink-0 text-xs text-muted-foreground">{o.hint}</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
